import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { MessageCircle, Package } from "lucide-react";
import { NavBar } from "@/components/NavBar";
import { Skeleton } from "@/components/ui/skeleton";
import { api, imageUrl, formatINR } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { OrderStickerButton } from "@/components/OrderSticker";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "confirmed", label: "Confirmed" },
];
const toList = (d) => (Array.isArray(d) ? d : d?.orders || d?.items || []);
const listingCache = {};
const getListing = (id) => (listingCache[id] ||= api.get(`/listings/${id}`).then((r) => r.data).catch(() => { delete listingCache[id]; return null; }));

const enrich = async (o) => {
  if (o.listing_title && o.listing_image !== undefined) return o;
  const l = o.listing || (o.listing_id ? await getListing(o.listing_id) : null);
  return { ...o, listing_title: o.listing_title || l?.title, listing_image: o.listing_image ?? l?.images?.[0], seller_name: o.seller_name || l?.seller_name, delivery_time: o.delivery_time || l?.delivery_time, unit_price: o.unit_price || l?.price };
};

export default function Orders() {
  usePageTitle("My orders");
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;
    let ignore = false;
    setLoading(true);
    setError(false);
    api.get("/orders", { params: { role: "buyer", status: tab } })
      .then((r) => Promise.all(toList(r.data).map(enrich)))
      .then((list) => !ignore && setOrders(list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))))
      .catch(() => !ignore && setError(true))
      .finally(() => !ignore && setLoading(false));
    return () => { ignore = true; };
  }, [user, tab]);

  if (!authLoading && !user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="orders-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar />
      <main className="px-4 sm:px-8 lg:px-12 pt-36 lg:pt-40 pb-24 max-w-5xl mx-auto">
        <p className="eyebrow mb-3">Purchases</p>
        <h1 className="font-display text-4xl sm:text-5xl text-[#0F0F0F] mb-10">My Orders</h1>
        <div className="flex gap-8 border-b border-[#C5A059]/25 mb-10">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`orders-tab-${t.id}`} onClick={() => setTab(t.id)} className={`pb-3 -mb-px text-xs uppercase tracking-[0.28em] border-b-2 transition-colors ${tab === t.id ? "border-[#C5A059] text-[#0F0F0F]" : "border-transparent text-[#71717A] hover:text-[#0F0F0F]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {error ? (
          <p data-testid="orders-error" className="text-sm text-red-700">Couldn't load your orders. Please refresh.</p>
        ) : loading ? (
          <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-none bg-[#F5EFE6]" />)}</div>
        ) : orders.length === 0 ? (
          <div data-testid="orders-empty" className="py-16">
            <Package className="h-10 w-10 text-[#C5A059] mb-4" strokeWidth={1} />
            <p className="font-display text-2xl mb-2">{tab === "pending" ? "No orders awaiting confirmation." : "No confirmed orders yet."}</p>
            <Link to="/" data-testid="orders-browse-link" className="inline-block mt-6 border border-[#0F0F0F] px-8 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[#0F0F0F] hover:text-[#C5A059] transition-colors">Browse the edit</Link>
          </div>
        ) : (
          <ul data-testid="orders-list" className="space-y-4">
            {orders.map((o) => <OrderRow key={o.id} order={o} />)}
          </ul>
        )}
      </main>
    </div>
  );
}

const OrderRow = ({ order: o }) => {
  const qty = o.quantity || 1;
  const cash = (o.method || "").toLowerCase().startsWith("cash") || o.method === "cod";
  const rejected = o.status === "rejected";
  return (
    <li data-testid={`order-row-${o.id}`} className="bg-white border border-[#C5A059]/25 p-4 sm:p-5 flex gap-4 fade-up">
      <Link to={`/product/${o.listing_id}`} className="w-20 sm:w-24 aspect-[3/4] bg-[#F5EFE6] shrink-0 overflow-hidden">
        {o.listing_image && <img src={imageUrl(o.listing_image)} alt="" className="h-full w-full object-cover" />}
      </Link>
      <div className="min-w-0 flex-1 flex flex-col">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <Link to={`/product/${o.listing_id}`} data-testid="order-title" className="font-display text-lg leading-snug hover:text-[#B38F46] min-w-0 break-words">{o.listing_title || "Item"}</Link>
          <span data-testid="order-status" className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 shrink-0 ${rejected ? "bg-red-50 text-red-700" : o.status === "confirmed" ? "bg-[#0F0F0F] text-[#C5A059]" : "bg-[#F5EFE6] text-[#0F0F0F]"}`}>
            {rejected ? "Rejected" : o.status === "confirmed" ? "Confirmed" : "Awaiting seller"}
          </span>
        </div>
        {o.seller_name && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1">by {o.seller_name}</p>}
        <p className="text-sm text-[#3f3f46] mt-2">
          <span data-testid="order-total" className="font-mono">{formatINR((o.unit_price || 0) * qty)}</span>
          <span className="text-[#71717A]"> · Qty {qty} · {cash ? "Cash on Delivery" : "UPI"}</span>
        </p>
        {rejected && o.reject_note && <p className="text-xs text-red-700 mt-1">{o.reject_note}</p>}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-auto pt-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
            {o.created_at && new Date(o.created_at).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}
            {o.delivery_time && ` · ${o.delivery_time}`}
          </span>
          <div className="flex gap-2">
          {o.status === "confirmed" && <OrderStickerButton order={o} />}
          {o.conversation_id && (
            <Link to={`/messages/${o.conversation_id}`} data-testid={`order-chat-${o.id}`} className="flex items-center gap-2 border border-[#0F0F0F] px-4 py-2 text-[10px] tracking-[0.25em] uppercase hover:bg-[#0F0F0F] hover:text-[#C5A059] transition-colors">
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Chat
            </Link>
          )}
          </div>
        </div>
      </div>
    </li>
  );
};
